import { Pagination, SpatialEntity, Template } from '@ekkogmbh/apisdk';
import React, { useCallback, useMemo, useState } from 'react';
import CoordinateListItem from 'src/Common/Components/CoordinateListItem';
import PaginatedSelect from 'src/Common/Components/Forms/PaginatedSelect';
import { injectFakePagination } from 'src/Common/Helper/Pagination';
import useEkko from 'src/Common/Providers/EkkoProvider';

type TemplateSelection = Template | string | (Template | string)[];

interface Props {
  coordinate: SpatialEntity['coordinate'];
  /**
   * Fetches all available templates that can be linked to given coordinate.
   * Has no effect when "linkedOnly" is true.
   */
  linkableOnly?: boolean;
  /**
   * Fetches all templates linked to the given coordinate.
   */
  linkedOnly?: boolean;
  selected?: TemplateSelection;
  optional?: boolean;
  /**
   * A string will be returned when the selected entity could not be fetched.
   * This can happen when a referenced template is deleted after it is included in
   * another entity.
   */
  onChangeCallback: (selection: TemplateSelection | undefined) => void;
  onErrorCallback?: () => void;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
  forwardedRef?: React.ForwardedRef<HTMLDivElement>;
}

export const TemplatePicker = ({
  linkedOnly,
  linkableOnly,
  coordinate,
  multiple,
  selected,
  disabled,
  onChangeCallback,
  ...props
}: Props): React.JSX.Element => {
  const { api } = useEkko();
  const [templates, setTemplates] = useState<Template[]>();

  const fetchTemplates = useCallback(async () => {
    let result = [];
    if (linkedOnly) {
      result = await api.getCompartmentTemplates({ coordinate });
    } else {
      result = await api.getAvailableTemplates(coordinate, linkableOnly);
    }
    setTemplates(result);
    return result;
  }, [api, coordinate, linkableOnly, linkedOnly]);

  const fetchPaginatedTemplates = useCallback(
    async (pagination: Pagination) => {
      const result = await fetchTemplates();
      return injectFakePagination(result, pagination);
    },
    [fetchTemplates],
  );

  const findTemplate = useCallback(
    (name: string): Template | undefined => templates?.find((candidate) => candidate.name === name),
    [templates],
  );

  const value = useMemo((): Template | Template[] | undefined => {
    if (Array.isArray(selected)) {
      // find matching templates objects for strings
      return selected.reduce((carry, t) => {
        if (typeof t === 'string') {
          const entity = findTemplate(t);
          if (entity !== undefined) {
            carry.push(entity);
          }
        } else {
          carry.push(t);
        }

        return carry;
      }, [] as Template[]);
    }

    if (typeof selected === 'string') {
      return findTemplate(selected);
    }

    return selected;
  }, [selected, findTemplate]);

  if (coordinate === '') {
    return <></>;
  }

  return (
    <PaginatedSelect
      {...props}
      value={value}
      disabled={disabled === true || coordinate === ''}
      multiple={multiple}
      multipleVariant={'chip'}
      label={multiple ? 'Templates' : 'Template'}
      fetchPaginatedItems={fetchPaginatedTemplates}
      itemId={(t: Template) => t.coordinate + '::' + t.name}
      itemRender={(t: Template) =>
        multiple ? t.name : <CoordinateListItem label={t.name} coordinate={t.coordinate} />
      }
      onChange={(t) => {
        if (templates === undefined) {
          return;
        }
        onChangeCallback(t as TemplateSelection);
      }}
    />
  );
};
